import { useState, useEffect, useImperativeHandle, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Paper, Box } from '@material-ui/core';
import FiltersTable from '../../../components/FiltersTable';
import { generateFilterPayload, transformData, processFiltersByType } from '../../../components/FilterUtils';
import { commonStrings, certificationStrings } from '../../../components/StringBundle';
import { getCertificationListApi } from './Service';
import Snackbar from "../../../components/Snackbar";
import { GetCertificationGridColumns } from "./constants";
import { exportExcelData, convertToInt, transformKeysToTitleCase, formatDateISO } from "../../common";
import { useGlobalStyles } from '../../../components/globalStyles';
import { getMrindocumentdownload } from "../../../apis";
import { AuthContext } from '../../../context/auth-context';

const CertificationList = ({
  loadingExcelVal,
  exportRef,
  certificationStatus,
  updateTotalRecords,
  totalrecord,
  activepage,
  setActivepage,
  company,
}) => {
  const globalClasses = useGlobalStyles();
  const location = useLocation();

  const handleDownload = async(record, text) => {
    try {
      let response = await getMrindocumentdownload(record.file_name);
      if (response) {
        const baseSrc = `data:application/pdf;base64,${response.fileData}`;
        const link = document.createElement("a");
        link.href = baseSrc;
        link.download = response.fileName; 
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {
      setSnack({
        open: true,
        message: e.response?.data || commonStrings.serverError,
        severity: commonStrings.error,
      });
    }
  }
  const columns = GetCertificationGridColumns(handleDownload);
  const certificationString = certificationStrings.forms;

  const savedPageSize = sessionStorage.getItem("selectedpagesize");
  const initialPageSize = savedPageSize ? convertToInt(savedPageSize) : 10;
  const savedFilterRaw = sessionStorage.getItem(`FiltersTable.${location.pathname}`);
  const initialFilter = savedFilterRaw ? JSON.parse(savedFilterRaw) : [];

  const [allData, setAllData] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchValue, setSearchValue] = useState(initialFilter);
  const [showSnack, setSnack] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(async () => {
    if (certificationStatus !== null && certificationStatus !== '') {
      listApiCall(certificationStatus, searchValue, activepage);
    }
  }, [certificationStatus, company]);

  // Export method to parent
  useImperativeHandle(exportRef, () => ({
    exportExcel,
  }));

  // call list api on export excel button click
  function exportExcel() {
    callListApiExportExcel(certificationStatus, 1, totalrecord, searchValue);
  }

  // prepare payload for list api call
  function getSearchPayload(state, page, pagelimit, searchtext) {

    return transformData(
      generateFilterPayload(searchtext, {
        page: page,
        limit: pagelimit,
        status: state === "CE-ST-1" ? "ACTIVE" : "Expired",
        company_name: company,
      })
    );
  }

  async function listApiCall(certificationStatus, filters, page) {
    callListApi(certificationStatus, page, pageSize, filters);
  }

  // Change label headers in excel
  const headerRenameMap = {
    alert_initation_date1: "First Alert Initiation Date",
    alert_initation_date2: "Second Alert Initiation Date",
  };

  // list api call based on filter and status selected for export excel
  async function callListApiExportExcel(
    state = certificationStatus,
    page = activepage,
    pagelimit = totalrecord,
    searchtext = searchValue
  ) {
    try {
      setLoading(true);
      loadingExcelVal(true);
      const searchPayload = getSearchPayload(state, page, pagelimit, searchtext);
      const response = await getCertificationListApi(searchPayload);
      if (response.certification_grid_list) {
        const transformedData = response.certification_grid_list.map(item => {
          const renamedItem = {};
          Object.keys(item).forEach(key => {
            renamedItem[headerRenameMap[key] || key] = item[key];
          });
          return transformKeysToTitleCase(renamedItem);
        });
        exportExcelData(transformedData, certificationString.certification);
      }
    } catch (e) {
      setSnack({
        open: true,
        message: e.response?.data || commonStrings.serverError,
        severity: commonStrings.error,
      });
    } finally {
      loadingExcelVal(false);
      setLoading(false);
    }
  }

  // list api call based on filter and status selected
  async function callListApi(
    state = certificationStatus,
    page = activepage,
    pagelimit = pageSize,
    searchtext = searchValue) {
    setAllData(null);
    try {
      const searchPayload = getSearchPayload(state, page, pagelimit, searchtext);
      const response = await getCertificationListApi(searchPayload);
      if (response.certification_grid_list) {
        const dataWithKeys = response.certification_grid_list.map(item => ({
          ...item,
          key: item.record_id
        }));
        setAllData(dataWithKeys);
        updateTotalRecords(response?.total_count);
      } else {
        setAllData([]);
        updateTotalRecords(0);
      }
    } catch (e) {
      setHasError(true);
      setAllData([]);
      updateTotalRecords?.(0);
      setSnack({
        open: true,
        message: e.response?.data || commonStrings.serverError,
        severity: commonStrings.error,
      });
    }
  }

  //Pagination Function
  async function paginationChange(page, pagesize) {
    setActivepage(page);
    setPageSize(pagesize);
    callListApi(certificationStatus, page, pagesize, searchValue);
  }

  // function to filter data based on column search
  async function filterDataColumn(searchArr) {
    setSearchValue(searchArr === "" ? [] : searchArr);
    callListApi(certificationStatus, 1, pageSize, searchArr === "" ? [] : searchArr);
  }

  // Snackbar close function
  const snackbarClose = () => {
    setSnack({ open: false, message: "", severity: "" });
  };

  return (
    <Paper className={globalClasses.qliroot}>
      {showSnack.open && (<Snackbar {...showSnack} handleClose={snackbarClose} />)}
      {loading && (
        <Box className="overlay_css">
          <p>
            {commonStrings.dataLoading}
          </p>
        </Box>
      )}
      <FiltersTable
        columns={columns}
        tableData={allData}
        onPaginationChange={paginationChange}
        totalrecord={totalrecord}
        filterDataColumn={filterDataColumn}
        apiFilter={true}
        hasError={hasError}
        pagination={activepage}
      />
    </Paper>
  );
};

export default CertificationList;